import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import { useAuth } from '../../contexts/AuthContext';

const PageHeader = styled.div`
  margin-bottom: 24px;
`;

const Title = styled.h1`
  font-size: 28px;
  font-weight: 700;
  color: #1a1f2e;
  margin: 0 0 8px;
`;

const Subtitle = styled.p`
  font-size: 14px;
  color: #64748b;
  margin: 0;
`;

const GamesGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(300px, 1fr));
  gap: 20px;
`;

const GameCard = styled.div`
  background: #fff;
  border-radius: 16px;
  overflow: hidden;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.05);
  border: 1px solid #e2e8f0;
`;

const GameImageWrapper = styled.div`
  height: 160px;
  background: linear-gradient(135deg, #f0fdf9 0%, #e0f7f4 100%);
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
  position: relative;
`;

const GameImage = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
`;

const GameImageFallback = styled.div`
  font-size: 48px;
`;

const GameContent = styled.div`
  padding: 20px;
`;

const GameName = styled.h3`
  font-size: 16px;
  font-weight: 600;
  color: #1a1f2e;
  margin: 0 0 8px;
`;

const GameSlug = styled.div`
  font-size: 12px;
  color: #64748b;
  margin-bottom: 16px;
  font-family: monospace;
  background: #f1f5f9;
  padding: 4px 8px;
  border-radius: 4px;
  display: inline-block;
`;

const GameMeta = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-bottom: 16px;
`;

const Badge = styled.span<{ $type?: string }>`
  padding: 4px 10px;
  border-radius: 20px;
  font-size: 11px;
  font-weight: 600;
  background: ${p => 
    p.$type === 'premium' ? 'rgba(245, 158, 11, 0.1)' :
    p.$type === 'new' ? 'rgba(34, 197, 94, 0.1)' :
    p.$type === 'inactive' ? 'rgba(239, 68, 68, 0.1)' :
    'rgba(100, 116, 139, 0.1)'
  };
  color: ${p => 
    p.$type === 'premium' ? '#d97706' :
    p.$type === 'new' ? '#16a34a' :
    p.$type === 'inactive' ? '#dc2626' :
    '#64748b'
  };
`;

const GameActions = styled.div`
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
`;

const ToggleBtn = styled.button<{ $active: boolean }>`
  padding: 8px 14px;
  border: 1px solid ${p => p.$active ? '#2DBCAF' : '#e2e8f0'};
  border-radius: 8px;
  background: ${p => p.$active ? 'rgba(45, 188, 175, 0.1)' : '#fff'};
  color: ${p => p.$active ? '#2DBCAF' : '#64748b'};
  font-size: 12px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;
  display: flex;
  align-items: center;
  gap: 6px;

  &:hover {
    border-color: #2DBCAF;
  }
`;

const StatsRow = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
  padding: 12px 0;
  border-top: 1px solid #f1f5f9;
  margin-top: 16px;
`;

const LevelSettings = styled.div`
  margin-top: 16px;
  padding: 16px;
  background: #f8fafc;
  border-radius: 12px;
  border: 1px solid #e2e8f0;
`;

const LevelSettingsTitle = styled.div`
  font-size: 13px;
  font-weight: 600;
  color: #475569;
  margin-bottom: 12px;
  display: flex;
  align-items: center;
  gap: 6px;
`;

const LevelInputRow = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 8px;
  &:last-child { margin-bottom: 0; }
`;

const LevelLabel = styled.label`
  font-size: 12px;
  color: #64748b;
  min-width: 120px;
`;

const LevelInput = styled.input`
  width: 60px;
  padding: 6px 10px;
  border: 1px solid #e2e8f0;
  border-radius: 6px;
  font-size: 13px;
  text-align: center;
  &:focus {
    outline: none;
    border-color: #2DBCAF;
  }
`;

const LevelSaveBtn = styled.button`
  margin-top: 12px;
  padding: 8px 16px;
  background: #2DBCAF;
  color: white;
  border: none;
  border-radius: 8px;
  font-size: 12px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s;
  &:hover { background: #26a89c; }
  &:disabled { background: #94a3b8; cursor: not-allowed; }
`;

const Stat = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 13px;
  color: #64748b;
`;

const LoadingState = styled.div`
  text-align: center;
  padding: 60px 20px;
  color: #64748b;
`;

const EmptyState = styled.div`
  text-align: center;
  padding: 60px 20px;
  color: #64748b;
  background: #fff;
  border-radius: 16px;
  border: 1px solid #e2e8f0;
`;

// Coloring Template Modal Styles
const ModalOverlay = styled.div`
  position: fixed; inset: 0; z-index: 1000;
  background: rgba(0,0,0,0.5); backdrop-filter: blur(4px);
  display: flex; align-items: center; justify-content: center;
  padding: 20px;
`;

const ModalContent = styled.div`
  background: #fff; border-radius: 20px;
  width: 100%; max-width: 800px; max-height: 85vh;
  overflow: hidden; display: flex; flex-direction: column;
`;

const ModalHeader = styled.div`
  padding: 20px 24px; border-bottom: 1px solid #e2e8f0;
  display: flex; justify-content: space-between; align-items: center;
`;

const ModalTitle = styled.h2`
  font-size: 20px; font-weight: 700; color: #1a1f2e; margin: 0;
`;

const CloseBtn = styled.button`
  width: 36px; height: 36px; border-radius: 50%; border: none;
  background: #f1f5f9; cursor: pointer; font-size: 20px;
  display: flex; align-items: center; justify-content: center;
  &:hover { background: #e2e8f0; }
`;

const ModalBody = styled.div`
  flex: 1; overflow-y: auto; padding: 20px 24px;
`;

const ModalFooter = styled.div`
  padding: 16px 24px; border-top: 1px solid #e2e8f0;
  display: flex; justify-content: flex-end; gap: 12px;
`;

const TemplateRow = styled.div`
  display: flex; align-items: center; gap: 12px;
  padding: 12px; border-radius: 12px; background: #f8fafc;
  margin-bottom: 8px;
`;

const TemplatePreview = styled.div`
  width: 50px; height: 50px; border-radius: 8px;
  background: #fff; border: 1px solid #e2e8f0;
  overflow: hidden; flex-shrink: 0;
  img { width: 100%; height: 100%; object-fit: contain; }
`;

const TemplateInfo = styled.div`
  flex: 1; min-width: 0;
`;

const TemplateNameInput = styled.input`
  font-size: 14px; font-weight: 600; border: 1px solid transparent;
  padding: 4px 8px; border-radius: 4px; width: 140px;
  &:hover { border-color: #e2e8f0; }
  &:focus { outline: none; border-color: #2DBCAF; }
`;

const TemplateId = styled.div`
  font-size: 11px; color: #94a3b8; font-family: monospace;
`;

const MoveBtn = styled.button`
  width: 28px; height: 24px; border: none;
  background: #e2e8f0; border-radius: 4px; cursor: pointer;
  &:hover { background: #cbd5e1; }
  &:disabled { opacity: 0.3; cursor: not-allowed; }
`;

const ToggleSmall = styled.label<{ $checked?: boolean; $premium?: boolean }>`
  display: flex; align-items: center; gap: 4px;
  padding: 4px 10px; border-radius: 6px; cursor: pointer;
  font-size: 11px; font-weight: 600;
  background: ${p => p.$checked ? (p.$premium ? '#fef3c7' : '#dcfce7') : '#f1f5f9'};
  color: ${p => p.$checked ? (p.$premium ? '#d97706' : '#16a34a') : '#64748b'};
  input { display: none; }
`;

const SaveBtn = styled.button<{ $primary?: boolean }>`
  padding: 10px 20px; border-radius: 10px; font-size: 14px;
  font-weight: 600; cursor: pointer; border: none;
  background: ${p => p.$primary ? '#2DBCAF' : '#f1f5f9'};
  color: ${p => p.$primary ? '#fff' : '#64748b'};
  &:hover { background: ${p => p.$primary ? '#1fa99c' : '#e2e8f0'}; }
  &:disabled { opacity: 0.5; cursor: not-allowed; }
`;

const ColoringBtn = styled.button`
  padding: 8px 12px; border: 1px solid #8b5cf6;
  border-radius: 8px; background: rgba(139, 92, 246, 0.1);
  color: #8b5cf6; font-size: 12px; font-weight: 500;
  cursor: pointer; display: flex; align-items: center; gap: 6px;
  &:hover { background: rgba(139, 92, 246, 0.2); }
`;

interface ColoringTemplate {
  _id: string;
  templateId: string;
  name: string;
  src: string;
  order: number;
  isPremium: boolean;
  isActive: boolean;
}

interface Game {
  _id: string;
  name: string;
  slug: string;
  image?: string;
  path: string;
  premium: boolean;
  isActive: boolean;
  isNew: boolean;
  order: number;
  playCount: number;
  categories: Array<{ name: string; slug: string }>;
  ageGroups: Array<{ name: string; slug: string }>;
  // Level-based settings (for games like shape-match)
  totalLevels?: number;
  loginRequiredLevel?: number;
  premiumStartLevel?: number;
}

const GamesPage: React.FC = () => {
  const { token } = useAuth();
  const [games, setGames] = useState<Game[]>([]);
  const [loading, setLoading] = useState(true);
  const [levelEdits, setLevelEdits] = useState<Record<string, { login: number; premium: number }>>({});
  const [saving, setSaving] = useState<string | null>(null);
  
  // Coloring templates state
  const [showColoringModal, setShowColoringModal] = useState(false);
  const [coloringTemplates, setColoringTemplates] = useState<ColoringTemplate[]>([]);
  const [coloringLoading, setColoringLoading] = useState(false);
  const [coloringHasChanges, setColoringHasChanges] = useState(false);
  const [coloringSaving, setColoringSaving] = useState(false);

  useEffect(() => {
    const fetchGames = async () => {
      try {
        const res = await fetch('/api/admin/games', { credentials: 'include' });
        if (res.ok) {
          const data = await res.json();
          setGames(data.games);
        }
      } catch (err) {
        console.error('Failed to fetch games:', err);
      } finally {
        setLoading(false);
      }
    };
    fetchGames();
  }, []);

  const handleToggle = async (gameId: string, field: 'isActive' | 'premium' | 'isNew', currentValue: boolean) => {
    try {
      const res = await fetch(`/api/admin/games/${gameId}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ [field]: !currentValue }),
      });
      
      if (res.ok) {
        setGames(games.map(g => g._id === gameId ? { ...g, [field]: !currentValue } : g));
      }
    } catch (err) {
      console.error('Failed to update game:', err);
    }
  };

  const getGameEmoji = (slug: string) => {
    const emojis: Record<string, string> = {
      puzzle: '🧩',
      memory: '🧠',
      color: '🎨',
      'letter-match': '🔤',
      animal: '🦁',
      parrot: '🦜',
      shadow: '👤',
      maze: '🌀',
      'shape-match': '🔷',
    };
    return emojis[slug] || '🎮';
  };

  const handleLevelChange = (gameId: string, field: 'login' | 'premium', value: string) => {
    const num = value === '' ? '' : parseInt(value);
    const game = games.find(g => g._id === gameId);
    if (!game) return;
    
    setLevelEdits(prev => {
      const current = prev[gameId] || {
        login: game.loginRequiredLevel ?? 2,
        premium: game.premiumStartLevel ?? 6,
      };
      return {
        ...prev,
        [gameId]: {
          ...current,
          [field]: num,
        }
      };
    });
  };

  const saveLevelSettings = async (game: Game) => {
    const edits = levelEdits[game._id];
    if (!edits) return;
    
    const loginVal = typeof edits.login === 'number' ? edits.login : parseInt(String(edits.login)) || 2;
    const premiumVal = typeof edits.premium === 'number' ? edits.premium : parseInt(String(edits.premium)) || 6;
    
    setSaving(game._id);
    try {
      const res = await fetch(`/api/admin/games/${game._id}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ 
          loginRequiredLevel: loginVal,
          premiumStartLevel: premiumVal 
        }),
      });
      
      if (res.ok) {
        setGames(games.map(g => g._id === game._id ? { 
          ...g, 
          loginRequiredLevel: loginVal,
          premiumStartLevel: premiumVal 
        } : g));
        // Clear edits after save
        setLevelEdits(prev => {
          const { [game._id]: _, ...rest } = prev;
          return rest;
        });
        alert('Berhasil disimpan!');
      } else {
        alert('Gagal menyimpan!');
      }
    } catch (err) {
      console.error('Failed to save level settings:', err);
      alert('Error: ' + err);
    } finally {
      setSaving(null);
    }
  };

  // Coloring template functions
  const fetchColoringTemplates = async () => {
    setColoringLoading(true);
    try {
      const res = await fetch('/api/coloring-templates/admin', {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.ok) {
        setColoringTemplates(await res.json());
      }
    } catch (err) {
      console.error('Failed to fetch coloring templates:', err);
    } finally {
      setColoringLoading(false);
    }
  };

  const seedColoringTemplates = async () => {
    try {
      const res = await fetch('/api/coloring-templates/seed', {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.ok) {
        fetchColoringTemplates();
      } else {
        const data = await res.json();
        alert(data.error || 'Gagal seed data');
      }
    } catch (err) {
      alert('Gagal seed data');
    }
  };

  const openColoringModal = () => {
    setShowColoringModal(true);
    fetchColoringTemplates();
  };

  const updateColoringTemplate = (id: string, updates: Partial<ColoringTemplate>) => {
    setColoringTemplates(prev => prev.map(t => t._id === id ? { ...t, ...updates } : t));
    setColoringHasChanges(true);
  };

  const moveColoringTemplate = (index: number, direction: 'up' | 'down') => {
    const newIndex = direction === 'up' ? index - 1 : index + 1;
    if (newIndex < 0 || newIndex >= coloringTemplates.length) return;
    
    const newTemplates = [...coloringTemplates];
    const [moved] = newTemplates.splice(index, 1);
    newTemplates.splice(newIndex, 0, moved);
    newTemplates.forEach((t, i) => { t.order = i; });
    
    setColoringTemplates(newTemplates);
    setColoringHasChanges(true);
  };

  const saveColoringTemplates = async () => {
    setColoringSaving(true);
    try {
      for (const t of coloringTemplates) {
        await fetch(`/api/coloring-templates/${t._id}`, {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
          body: JSON.stringify({ name: t.name, isPremium: t.isPremium, isActive: t.isActive, order: t.order }),
        });
      }
      alert('Berhasil disimpan!');
      setColoringHasChanges(false);
    } catch (err) {
      alert('Gagal menyimpan');
    } finally {
      setColoringSaving(false);
    }
  };

  if (loading) {
    return <LoadingState>Loading games...</LoadingState>;
  }

  return (
    <>
      <PageHeader>
        <Title>Games</Title>
        <Subtitle>Kelola game yang tersedia di Kindergame</Subtitle>
      </PageHeader>

      {games.length === 0 ? (
        <EmptyState>Tidak ada game ditemukan</EmptyState>
      ) : (
        <GamesGrid>
          {games.map((game) => (
            <GameCard key={game._id}>
              <GameImageWrapper>
                {game.image ? (
                  <GameImage src={game.image} alt={game.name} />
                ) : (
                  <GameImageFallback>{getGameEmoji(game.slug)}</GameImageFallback>
                )}
              </GameImageWrapper>
              <GameContent>
                <GameName>{game.name}</GameName>
                <GameSlug>/{game.slug}</GameSlug>

                <GameMeta>
                  {!game.isActive && <Badge $type="inactive">Tidak Aktif</Badge>}
                  {game.premium && <Badge $type="premium">Premium</Badge>}
                  {game.isNew && <Badge $type="new">Baru</Badge>}
                  {game.categories?.map(cat => (
                    <Badge key={cat.slug}>{cat.name}</Badge>
                  ))}
                </GameMeta>

                <GameActions>
                  <ToggleBtn 
                    $active={game.isActive}
                    onClick={() => handleToggle(game._id, 'isActive', game.isActive)}
                  >
                    {game.isActive ? '✓ Aktif' : '○ Nonaktif'}
                  </ToggleBtn>
                  <ToggleBtn 
                    $active={game.premium}
                    onClick={() => handleToggle(game._id, 'premium', game.premium)}
                  >
                    {game.premium ? '★ Premium' : '☆ Free'}
                  </ToggleBtn>
                  <ToggleBtn 
                    $active={game.isNew}
                    onClick={() => handleToggle(game._id, 'isNew', game.isNew)}
                  >
                    {game.isNew ? '🆕 New' : '○ Normal'}
                  </ToggleBtn>
                  {game.slug === 'coloring' && (
                    <ColoringBtn onClick={openColoringModal}>
                      🎨 Kelola Templates
                    </ColoringBtn>
                  )}
                </GameActions>

                <StatsRow>
                  <Stat>
                    <span>🎯</span>
                    {game.playCount?.toLocaleString() || 0} plays
                  </Stat>
                  <Stat>
                    <span>📊</span>
                    Order: {game.order}
                  </Stat>
                </StatsRow>

                {/* Level Settings for games with levels */}
                {game.totalLevels && (
                  <LevelSettings>
                    <LevelSettingsTitle>
                      🎮 Pengaturan Level ({game.totalLevels} total)
                    </LevelSettingsTitle>
                    <LevelInputRow>
                      <LevelLabel>Login mulai level:</LevelLabel>
                      <LevelInput
                        type="number"
                        min="1"
                        max={game.totalLevels}
                        value={levelEdits[game._id]?.login !== undefined ? levelEdits[game._id].login : (game.loginRequiredLevel ?? 2)}
                        onChange={(e) => handleLevelChange(game._id, 'login', e.target.value)}
                      />
                    </LevelInputRow>
                    <LevelInputRow>
                      <LevelLabel>Premium mulai level:</LevelLabel>
                      <LevelInput
                        type="number"
                        min="1"
                        max={game.totalLevels}
                        value={levelEdits[game._id]?.premium !== undefined ? levelEdits[game._id].premium : (game.premiumStartLevel ?? 6)}
                        onChange={(e) => handleLevelChange(game._id, 'premium', e.target.value)}
                      />
                    </LevelInputRow>
                    {levelEdits[game._id] && (
                      <LevelSaveBtn 
                        onClick={() => saveLevelSettings(game)}
                        disabled={saving === game._id}
                      >
                        {saving === game._id ? 'Menyimpan...' : '💾 Simpan'}
                      </LevelSaveBtn>
                    )}
                  </LevelSettings>
                )}
              </GameContent>
            </GameCard>
          ))}
        </GamesGrid>
      )}

      {/* Coloring Templates Modal */}
      {showColoringModal && (
        <ModalOverlay onClick={() => setShowColoringModal(false)}>
          <ModalContent onClick={e => e.stopPropagation()}>
            <ModalHeader>
              <ModalTitle>🎨 Kelola Template Mewarnai</ModalTitle>
              <CloseBtn onClick={() => setShowColoringModal(false)}>×</CloseBtn>
            </ModalHeader>
            <ModalBody>
              {coloringLoading ? (
                <LoadingState>Memuat...</LoadingState>
              ) : coloringTemplates.length === 0 ? (
                <EmptyState>
                  <p>Belum ada template.</p>
                  <SaveBtn $primary onClick={seedColoringTemplates} style={{ marginTop: 12 }}>
                    Seed Data Awal
                  </SaveBtn>
                </EmptyState>
              ) : (
                coloringTemplates.map((t, idx) => (
                  <TemplateRow key={t._id}>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                      <MoveBtn onClick={() => moveColoringTemplate(idx, 'up')} disabled={idx === 0}>▲</MoveBtn>
                      <MoveBtn onClick={() => moveColoringTemplate(idx, 'down')} disabled={idx === coloringTemplates.length - 1}>▼</MoveBtn>
                    </div>
                    <div style={{ width: 24, textAlign: 'center', color: '#94a3b8', fontWeight: 600 }}>{idx + 1}</div>
                    <TemplatePreview><img src={t.src} alt={t.name} /></TemplatePreview>
                    <TemplateInfo>
                      <TemplateNameInput
                        value={t.name}
                        onChange={e => updateColoringTemplate(t._id, { name: e.target.value })}
                      />
                      <TemplateId>{t.templateId}</TemplateId>
                    </TemplateInfo>
                    <ToggleSmall $checked={t.isActive}>
                      <input type="checkbox" checked={t.isActive} onChange={e => updateColoringTemplate(t._id, { isActive: e.target.checked })} />
                      {t.isActive ? '✓ Aktif' : 'Off'}
                    </ToggleSmall>
                    <ToggleSmall $checked={t.isPremium} $premium>
                      <input type="checkbox" checked={t.isPremium} onChange={e => updateColoringTemplate(t._id, { isPremium: e.target.checked })} />
                      {t.isPremium ? '⭐ Premium' : 'Free'}
                    </ToggleSmall>
                  </TemplateRow>
                ))
              )}
            </ModalBody>
            {coloringHasChanges && (
              <ModalFooter>
                <SaveBtn onClick={() => { setColoringHasChanges(false); fetchColoringTemplates(); }}>Batal</SaveBtn>
                <SaveBtn $primary onClick={saveColoringTemplates} disabled={coloringSaving}>
                  {coloringSaving ? 'Menyimpan...' : 'Simpan Perubahan'}
                </SaveBtn>
              </ModalFooter>
            )}
          </ModalContent>
        </ModalOverlay>
      )}
    </>
  );
};

export default GamesPage;
